/**
 * AuthenticatedRootPage — landing screen for signed-in users.
 *
 * Auth-state branching (loading / unauthenticated → LoginPage / error →
 * Retry / authenticated) is owned by this component and is unchanged
 * from Simon's SCRUM-9 introduction (commit aff0d0d, 2026-04-30) — those
 * branches are still load-bearing for the auth shell.
 *
 * SCRUM-297 / MENU-1: the authenticated branch now renders `<PmsShellV4 />`
 * (epic SCRUM-296) — the single PMS shell that wraps every authenticated
 * route via `<Outlet/>`. It owns its own data plumbing (useCurrentUser /
 * useCurrentOrg / useUserPreferences / useMe) so this file stays a thin
 * auth-state router. The dashboard home content lives in the `path: ''`
 * index child (`DashboardHome`) and renders inside the shell's `<main>`.
 * (Previously this branch rendered the `DashboardPage` shell — SCRUM-58.)
 *
 * Revert recipe: swap `<PmsShellV4 />` back for the original Phase-0
 * `<main className="mx-auto max-w-3xl p-6">…<pre>{JSON.stringify(...)}` block
 * (see git show aff0d0d -- apps/admin/src/app/AuthenticatedRootPage.tsx).
 */

import * as React from 'react';
import { Button } from '@sparta/ui-kit';
import { useSession } from '../shared/hooks/useSession.js';
import { LoginPage } from './auth/LoginPage.js';
// Consume the shell widget via its public barrel — never reach into
// widgets/<name>/internals per ADR frontend-0001's bootstrap-vs-widget
// boundary.
import { PmsShellV4 } from '../widgets/pms-shell-v4/index.js';

export function AuthenticatedRootPage(): React.ReactElement {
  const session = useSession();

  if (session.status === 'loading') {
    return (
      <main className="flex min-h-screen items-center justify-center text-neutral-600">
        <p aria-live="polite">Loading…</p>
      </main>
    );
  }
  if (session.status === 'unauthenticated') {
    return <LoginPage />;
  }
  if (session.status === 'error') {
    return (
      <main className="mx-auto max-w-md p-6">
        <h1 className="mb-2 text-xl font-semibold">Couldn’t reach Artemis</h1>
        <p className="mb-4 text-sm text-neutral-700">
          We couldn’t reach the Artemis service. Check your connection and
          try again in a moment.
        </p>
        <Button onClick={() => session.refetch()}>Retry</Button>
      </main>
    );
  }

  // authenticated → render the single PMS shell. PmsShellV4 pulls user/
  // org/preferences/me via its own hooks and renders the active route via
  // <Outlet/>; we don't need to thread session here.
  return <PmsShellV4 />;
}
